import { Location } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, Subject, firstValueFrom, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { ComponentCanDeactivate } from '../../guards/unsaved-changes.guard';
import { LookupTypeCodes, LookupValue } from '../../models/lookup.models';
import { NotesWithMentionsDto } from '../../models/notes.models';
import { EmployeeAssignmentDto } from '../../models/request.models';
import { ResourceDetailDto } from '../../models/resource.models';
import { TimeOffEntry, TimeOffTypeLookup } from '../../models/time-off.models';
import { AssignmentService } from '../../services/assignment.service';
import { AuthService } from '../../services/auth.service';
import {
  Breadcrumb,
  BreadcrumbService,
} from '../../services/breadcrumb.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { FloatingDialogService } from '../../services/floating-dialog.service';
import { LookupService } from '../../services/lookup.service';
import {
  ExternalMatterDto as MatterSearchResultDto,
  MatterService,
} from '../../services/matter.service';
import { RequestService } from '../../services/request.service';
import { ResourceService } from '../../services/resource.service';
import { SnackbarService } from '../../services/snackbar.service';
import { TimeOffService } from '../../services/time-off.service';
import {
  AssignmentRow,
  AssignmentsCardComponent,
} from '../shared/assignments-card/assignments-card.component';
import { AssignmentNotesPanelComponent } from '../shared/assignment-notes-panel/assignment-notes-panel.component';
import { BreadcrumbComponent } from '../shared/breadcrumb/breadcrumb.component';
import { CapacityManagementComponent } from '../shared/capacity-management/capacity-management.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../shared/confirmation-dialog/confirmation-dialog.component';
import { EntityNotesComponent } from '../shared/entity-notes/entity-notes.component';
import { ResourceBillingHistoryComponent } from '../shared/resource-billing-history/resource-billing-history.component';
import { ResourceBillingSummaryComponent } from '../shared/resource-billing-summary/resource-billing-summary.component';
import { CopyableTextComponent } from '../shared/copyable-text/copyable-text.component';
import { MembershipChipsComponent } from '../shared/membership-chips/membership-chips.component';
import { ResourceAttributesComponent } from '../shared/resource-attributes/resource-attributes.component';
import { TimeOffManagementComponent } from '../shared/time-off-management/time-off-management.component';
import { describeApiError, toApiError } from '../../utils/api-error.utils';
import {
  applyAssignmentRowEdits,
  toAssignmentRow,
} from '../../utils/assignment-row.utils';

@Component({
  selector: 'app-resource-details',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule,
    RouterModule,
    ResourceBillingHistoryComponent,
    ResourceBillingSummaryComponent,
    CapacityManagementComponent,
    EntityNotesComponent,
    AssignmentNotesPanelComponent,
    AssignmentsCardComponent,
    TimeOffManagementComponent,
    BreadcrumbComponent,
    MembershipChipsComponent,
    ResourceAttributesComponent,
    CopyableTextComponent,
  ],
  templateUrl: './resource-details.component.html',
  styleUrls: ['./resource-details.component.scss'],
})
export class ResourceDetailsComponent
  implements OnInit, OnDestroy, ComponentCanDeactivate
{
  /**
   * Employee id. Set by Angular from the @Input when the component is
   * rendered in a floating dialog, or assigned from the route params
   * when rendered as a full page (see ngOnInit).
   */
  @Input() employeeId: string | null = null;

  /**
   * Auto-injected by FloatingDialogService when the component is loaded
   * into a floating dialog. Its presence is the signal that we're in
   * dialog mode (used by `isDialogMode`).
   */
  @Input() floatingDialogId?: string;

  get isDialogMode(): boolean {
    return !!this.floatingDialogId;
  }

  /**
   * Maps a floating-dialog instance's inputs to the full-page route for
   * this component. Read by FloatingDialogContainerComponent to render
   * the "Open in main window" button in the dialog header.
   */
  static getMainWindowRoute(
    inputs: Record<string, unknown>,
  ): unknown[] | null {
    const id = inputs?.['employeeId'];
    return typeof id === 'string' && id ? ['/resources', id] : null;
  }

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private resourceService = inject(ResourceService);
  private authService = inject(AuthService);
  private location = inject(Location);
  private requestService = inject(RequestService);
  private assignmentService = inject(AssignmentService);
  private matterService = inject(MatterService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private timeOffService = inject(TimeOffService);
  private lookupService = inject(LookupService);
  private featureFlagService = inject(FeatureFlagService);
  private floatingDialogService = inject(FloatingDialogService);
  private breadcrumbService = inject(BreadcrumbService);

  readonly capacityManagement = viewChild(CapacityManagementComponent);

  readonly entityNotes = viewChild(EntityNotesComponent);

  readonly timeOffManagement = viewChild(TimeOffManagementComponent);

  readonly timeOffSection = viewChild('timeOffSection', { read: ElementRef });

  readonly assignmentNotesSection = viewChild('assignmentNotesSection', {
    read: ElementRef,
  });

  resource: ResourceDetailDto | null = null;
  assignmentRows: AssignmentRow[] = [];
  isLoading = false;
  error: string | null = null;
  isCapacityReadOnly: boolean = true;
  isNotesReadOnly: boolean = true;
  isAttributesReadOnly: boolean = true;
  isTimeOffReadOnly: boolean = true;
  canEditEntitledDays: boolean = false;
  canApproveTimeOff: boolean = false;
  isTimeOffTrackingEnabled = false;
  isComparisonPopUpsEnabled = false;
  private destroy$ = new Subject<void>();

  // Assignments
  pendingAssignments: EmployeeAssignmentDto[] = [];
  isLoadingAssignments = false;
  /** The last assignments fetch failed; the Assignment Notes card shows its error. */
  assignmentsLoadFailed = false;
  /**
   * A silent refresh is in flight. A note edit made meanwhile is re-sent as a
   * fresh refresh (see onAssignmentChanged), so the older response cannot land
   * over it.
   */
  private silentRefreshPending = false;
  /**
   * Emits the employee id to load assignments for, and whether the load is silent
   * (see refreshAssignmentNotesSilently). Every load flows through the one
   * switchMap pipeline set up in the constructor, so only the latest request can
   * settle the list: reloads can overlap (a "Mark Active" refresh during an add or
   * remove), and the last response to arrive would otherwise win, stale or not.
   */
  private readonly assignmentsLoad$ = new Subject<{
    id: string;
    silent: boolean;
  }>();

  // Add-matter typeahead (direct assignment)
  canCreateAssignments = false;
  canDeleteAssignments = false;
  canEditAssignments = false;
  canReadResourceAssignmentNotes = false;
  canReadManagerAssignmentNotes = false;
  canWriteManagerAssignmentNotes = false;
  addMatterQuery = '';
  matterSuggestions: MatterSearchResultDto[] = [];
  isSearchingMatters = false;
  matterNotFound = false;
  isCreatingAssignment = false;
  /** Whether the inline add flow is expanded (false shows the "+ Add Assignment" button). */
  isAddingAssignment = false;
  readonly addAssignmentInput = viewChild<ElementRef<HTMLInputElement>>(
    'addAssignmentInput',
  );
  private addMatterSubject$ = new Subject<string>();

  // Staged direct assignment (after matter selected, before confirm)
  stagedMatter: MatterSearchResultDto | null = null;
  stagedStartDate: Date | null = null;
  stagedEndDate: Date | null = null;

  // Time off
  timeOffEntries: TimeOffEntry[] = [];
  timeOffTypeLookups: TimeOffTypeLookup[] = [];

  constructor() {
    // catchError sits on the inner observable, so a failed load becomes the
    // error state without completing the pipeline for later reloads.
    this.assignmentsLoad$
      .pipe(
        switchMap(({ id, silent }) =>
          this.assignmentService.getAssignmentsByEmployee(id).pipe(
            map((assignments) => ({ ok: true as const, silent, assignments })),
            catchError((error: unknown) =>
              of({ ok: false as const, silent, error }),
            ),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((outcome) => {
        // A silent refresh replaces only the list the Assignment Notes card
        // reads, and on failure keeps the current one: it is a background
        // refresh, with no loading or error state of its own.
        if (outcome.silent) {
          this.silentRefreshPending = false;
          if (outcome.ok) {
            this.pendingAssignments = outcome.assignments;
            // Fresh data, so an earlier failed load's error no longer applies.
            this.assignmentsLoadFailed = false;
          }
          return;
        }
        this.isLoadingAssignments = false;
        if (outcome.ok) {
          this.onAssignmentsLoaded(outcome.assignments);
        } else {
          this.onAssignmentsLoadFailed(outcome.error);
        }
      });
  }

  ngOnInit(): void {
    // Subscribe to role changes to update read-only state reactively
    this.authService.userRoles$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkUserPermissions();
    });

    // Also check immediately in case roles are already loaded
    this.checkUserPermissions();

    // Setup add-matter typeahead debouncing
    this.addMatterSubject$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        if (query.trim().length >= 3) {
          this.performMatterSearch(query.trim());
        } else {
          this.matterSuggestions = [];
          this.matterNotFound = false;
          this.isSearchingMatters = false;
        }
      });

    // Load feature flags
    this.featureFlagService
      .isFeatureFlagEnabled('time-off-tracking')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isTimeOffTrackingEnabled = enabled;
      });

    this.featureFlagService
      .isFeatureFlagEnabled('comparison-pop-ups')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isComparisonPopUpsEnabled = enabled;
      });

    if (this.isDialogMode) {
      this.updateTimeOffReadOnly();
      this.loadResource();
    } else {
      this.route.params.subscribe((params) => {
        const employeeId = params['employeeId'];
        if (employeeId) {
          this.employeeId = employeeId;
          this.updateTimeOffReadOnly();
          this.loadResource();
        } else {
          this.error = 'No employee ID provided';
        }
      });
    }

    // Subscribe to role changes to update read-only state reactively
    this.authService.userRoles$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isNotesReadOnly = !this.authService.canEditResources();
      this.updateTimeOffReadOnly();
    });

    // Also check immediately in case roles are already loaded
    this.isNotesReadOnly = !this.authService.canEditResources();
    this.updateTimeOffReadOnly();
  }

  canDeactivate(): boolean | Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.checkAllSectionsCanDeactivate()
        .then((canLeave) => {
          observer.next(canLeave);
          observer.complete();
        })
        .catch((error) => {
          // Fail open: a hung guard would trap the user on the page with no
          // feedback, which is worse than an exceptional-case navigation.
          console.error('Error checking unsaved changes on navigation:', error);
          observer.next(true);
          observer.complete();
        });
    });
  }

  /**
   * Checks each editable section in turn (time off, notes, capacity);
   * each prompts for its own unsaved changes. Stops at the first section that blocks.
   */
  private async checkAllSectionsCanDeactivate(): Promise<boolean> {
    const timeOffManagement = this.timeOffManagement();
    if (timeOffManagement?.hasUnsavedChanges()) {
      const canLeave = await timeOffManagement.checkUnsavedChangesForNavigation();
      if (!canLeave) return false;
    }

    const entityNotes = this.entityNotes();
    if (entityNotes?.hasUnsavedChanges()) {
      const canLeave = await entityNotes.checkUnsavedChangesForNavigation();
      if (!canLeave) return false;
    }

    const capacityManagement = this.capacityManagement();
    if (capacityManagement) {
      const result = capacityManagement.canDeactivate();
      const canLeave =
        result instanceof Observable ? await firstValueFrom(result) : result;
      if (!canLeave) return false;
    }

    return true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Refresh every permission-derived flag. Called both on userRoles$ emissions and
   * immediately on init — keeping it in one place stops the two call sites from
   * drifting apart when a flag is added.
   */
  private checkUserPermissions(): void {
    this.isCapacityReadOnly = !this.authService.canEditResourceCapacity();
    this.isAttributesReadOnly = !this.authService.canEditResourceAttributes();
    this.canCreateAssignments = this.authService.canCreateAssignments();
    this.canDeleteAssignments = this.authService.canDeleteAssignments();
    this.canEditAssignments = this.authService.canEditAssignments();
    this.canReadResourceAssignmentNotes =
      this.authService.canReadResourceAssignmentNotes();
    this.canReadManagerAssignmentNotes =
      this.authService.canReadManagerAssignmentNotes();
    this.canWriteManagerAssignmentNotes =
      this.authService.canWriteManagerAssignmentNotes();
  }

  private loadResource(): void {
    if (!this.employeeId) return;

    this.isLoading = true;
    this.error = null;

    this.resourceService.getResourceDetails(this.employeeId).subscribe({
      next: (resource) => {
        this.resource = resource;
        if (!this.isDialogMode) {
          this.breadcrumbService.setDynamicLabel(
            ':employeeId',
            resource.fullName,
          );
        }
        this.isLoading = false;
        // Load assignments after resource is loaded
        this.loadPendingAssignments();
        // Load time off mock data
        this.loadTimeOffData();
      },
      error: (error) => {
        // A 404 covers both a missing resource and one outside the caller's practice areas (the
        // API does not say which), and a 403 is a caller without Resources.Read following a link.
        // Any other failure is not about access, so it is not worded so.
        const apiError = toApiError(error);
        this.error =
          apiError.isNotFound || apiError.isForbidden
            ? 'This resource is unavailable. You may not have access to view resources within this practice area.'
            : 'Failed to load resource details. Please try again.';
        this.isLoading = false;
      },
    });
  }

  private loadPendingAssignments(): void {
    if (!this.employeeId) return;

    this.isLoadingAssignments = true;
    this.assignmentsLoadFailed = false;
    // Supersedes any silent refresh in flight (switchMap drops it).
    this.silentRefreshPending = false;
    this.assignmentsLoad$.next({ id: this.employeeId, silent: false });
  }

  /**
   * Refreshes only the DTO list the Assignment Notes card reads, for a new
   * assignment the Assignments card already shows. The table, its rows (which
   * the card tracks by identity, so replacing them would close an expanded
   * row) and the loading state are left alone. It runs through the same
   * pipeline as the full loads, so either one supersedes the other; but it
   * would cancel a full load in flight, which then never settles the table, so
   * in that case the full load is issued again instead.
   */
  private refreshAssignmentNotesSilently(): void {
    if (!this.employeeId) return;
    if (this.isLoadingAssignments) {
      this.loadPendingAssignments();
      return;
    }
    this.silentRefreshPending = true;
    this.assignmentsLoad$.next({ id: this.employeeId, silent: true });
  }

  private onAssignmentsLoaded(assignments: EmployeeAssignmentDto[]): void {
    this.pendingAssignments = assignments;
    this.assignmentRows = this.pendingAssignments.map((a) =>
      toAssignmentRow(a, {
        clientMatterNumber: a.clientMatterNumber,
        clientName: a.clientName,
        matterTitle: a.matterTitle,
        // Every row on this page belongs to the page's resource. Ownership itself
        // is derived by the card when the dialog opens, so the current user does
        // not have to be loaded yet.
        employeeId: this.employeeId ?? undefined,
        dialogTitle: a.matterTitle || 'Matter',
        resourceName: this.resource?.fullName || '',
      }),
    );
  }

  private onAssignmentsLoadFailed(error: unknown): void {
    console.error('Failed to load assignments:', error);
    this.pendingAssignments = [];
    this.assignmentsLoadFailed = true;
    // assignmentRows is what the card renders, so clearing only the DTOs
    // would leave the previous resource's rows under this resource's header.
    // Failure path only — a reload after add/remove must not blank the table.
    this.assignmentRows = [];
  }

  goBack(): void {
    this.location.back();
  }

  retry(): void {
    this.loadResource();
  }

  assignToMatter(): void {
    // TODO: Implement assign to matter functionality
    console.log(
      'Assign to matter clicked for resource:',
      this.resource?.fullName,
    );
  }

  getInitials(fullName: string): string {
    if (!fullName) return '';

    const names = fullName.trim().split(' ');
    if (names.length === 0) return '';

    const firstInitial = names[0].charAt(0).toUpperCase();
    const lastInitial =
      names.length > 1 ? names[names.length - 1].charAt(0).toUpperCase() : '';

    return firstInitial + lastInitial;
  }

  getPracticeAreasText(practiceAreas: any[]): string {
    if (!practiceAreas || practiceAreas.length === 0) {
      return 'Practice Area';
    }
    return practiceAreas.map((pa) => pa.description).join(' • ');
  }


  // Pending assignments navigation
  navigateToMatter(clientMatterNumber: string): void {
    const currentCrumbs = this.breadcrumbService.getCurrentBreadcrumbs();
    this.router.navigate(['/matters', clientMatterNumber], {
      state: {
        breadcrumbOverride: [...currentCrumbs, { label: ':matterId', url: '' }],
      },
    });
  }

  getResourceBreadcrumbContext(): Breadcrumb[] {
    return this.breadcrumbService.getCurrentBreadcrumbs();
  }

  // Notes update handler
  onNotesSaved(notesWithMentions: NotesWithMentionsDto): void {
    if (this.resource) {
      // Update the entire notesInfo object with the new data from backend
      this.resource.notesInfo = notesWithMentions;
    }
  }

  // Edit mode coordination between components: only one section may be in edit
  // mode at a time, so entering one first tries to exit the others.
  async onNotesEditModeRequested(): Promise<void> {
    if (!(await this.tryExitOtherEditModes('notes'))) return;
    this.entityNotes()?.enterEditMode();
  }

  async onCapacityEditModeRequested(): Promise<void> {
    if (!(await this.tryExitOtherEditModes('capacity'))) return;
    this.capacityManagement()?.enterEditMode();
  }

  async onTimeOffEditModeRequested(): Promise<void> {
    if (!(await this.tryExitOtherEditModes('timeOff'))) return;
    this.timeOffManagement()?.enterEditMode();
  }

  private async tryExitOtherEditModes(
    except: 'notes' | 'capacity' | 'timeOff',
  ): Promise<boolean> {
    const capacityManagement = this.capacityManagement();
    if (except !== 'capacity' && capacityManagement?.isEditMode) {
      if (!(await capacityManagement.tryExitEditMode())) return false;
    }

    const entityNotes = this.entityNotes();
    if (except !== 'notes' && entityNotes?.isEditMode) {
      if (!(await entityNotes.tryExitEditMode())) return false;
    }

    const timeOffManagement = this.timeOffManagement();
    if (except !== 'timeOff' && timeOffManagement?.isEditMode) {
      if (!(await timeOffManagement.tryExitEditMode())) return false;
    }

    return true;
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'Not set';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  // Time Off methods
  private loadTimeOffData(): void {
    if (!this.employeeId) return;

    // Load lookup types for the dialog
    this.lookupService
      .getLookupValues(LookupTypeCodes.TIME_OFF_REQUEST_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (values: LookupValue[]) => {
          this.timeOffTypeLookups = values
            .filter((v) => v.isActive)
            .map((v) => ({
              id: v.id,
              code: v.code,
              name: v.name,
              sortOrder: v.sortOrder,
              isDefault: v.isDefault,
            }));
        },
        error: (error) => {
          console.error('Failed to load time off type lookups:', error);
        },
      });

    // Load time off requests from API
    this.timeOffService
      .getByEmployeeId(this.employeeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (requests) => {
          this.timeOffEntries = requests.map((r) => ({
            id: r.id,
            displayStartDate: r.displayStartDate
              ? r.displayStartDate.substring(0, 10)
              : null,
            displayEndDate: r.displayEndDate
              ? r.displayEndDate.substring(0, 10)
              : null,
            type: r.typeName,
            typeLookupValueId: r.typeLookupValueId,
            typeCode: r.typeCode,
            statusCode: r.statusCode,
            days: r.days,
            carryOverDays: r.carryOverDays,
            daysOff: r.daysOff,
            hasNotes: r.hasNotes,
            hasManagerNotes: r.hasManagerNotes ?? false,
            notesInfo: r.notesInfo ?? null,
          }));
        },
        error: (error) => {
          console.error('Failed to load time off data:', error);
          this.timeOffEntries = [];
        },
      });
  }

  scrollToTimeOff(): void {
    this.timeOffSection()?.nativeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  openInFloatingDialog(): void {
    if (!this.resource || !this.employeeId) {
      return;
    }
    this.floatingDialogService.open(ResourceDetailsComponent, this.resource.fullName, {
      employeeId: this.employeeId,
    });
  }

  /**
   * The assignments card's "View all notes" action. Aligned to the top rather
   * than centred like scrollToTimeOff: the card is tall, and centring it would
   * push its header off-screen. Focus moves with it, for keyboard and
   * screen-reader users.
   */
  scrollToAssignmentNotes(): void {
    const section = this.assignmentNotesSection()?.nativeElement;
    if (!section) return;
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    section.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
    // Without this, focus stays on the button and the next Tab lands back in
    // the Assignments table, now off-screen. preventScroll keeps focus from
    // cutting the smooth scroll short.
    section.focus({ preventScroll: true });
  }

  scrollToTop(): void {
    const pageMain = document.querySelector('.page-main');
    if (pageMain) {
      pageMain.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  openTimeOffDialog(): void {
    // Legacy method - time off is now managed inline via TimeOffManagementComponent
  }

  private updateTimeOffReadOnly(): void {
    const hasPermission = this.authService.canSetResourceTimeOff();
    this.isTimeOffReadOnly = !hasPermission;
    this.canEditEntitledDays = this.authService.canEditResourceTimeOffPolicy();
    this.canApproveTimeOff = this.authService.canApproveTimeOff();
  }

  // Time off save handler (called by TimeOffManagementComponent after successful save)
  onTimeOffSaved(): void {
    this.loadTimeOffData();
  }

  onEntitledDaysSaved(): void {
    this.loadTimeOffData();
  }

  // ---------------------------------------------------------------------------
  // Direct assignment management (bypass the Request workflow)
  // ---------------------------------------------------------------------------

  startAddAssignment(): void {
    this.isAddingAssignment = true;
    // The input is inside an @if, so focus after the next change-detection pass.
    setTimeout(() => this.addAssignmentInput()?.nativeElement.focus());
  }

  cancelAddAssignment(): void {
    if (this.isCreatingAssignment) return;
    this.onCancelStagedAssignment();
    this.clearAddMatterTypeahead();
    this.isAddingAssignment = false;
  }

  onAddMatterInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.addMatterQuery = value;
    this.isSearchingMatters = value.trim().length >= 3;
    this.matterNotFound = false;
    this.addMatterSubject$.next(value);
  }

  clearAddMatterTypeahead(): void {
    this.addMatterQuery = '';
    this.matterSuggestions = [];
    this.matterNotFound = false;
    this.isSearchingMatters = false;
    this.addMatterSubject$.next('');
  }

  onAddMatterBlur(): void {
    setTimeout(() => {
      this.matterSuggestions = [];
      this.matterNotFound = false;
    }, 200);
  }

  onSelectMatterToAssign(matter: MatterSearchResultDto): void {
    if (!this.employeeId || this.isCreatingAssignment) return;
    this.stagedMatter = matter;
    this.stagedStartDate = null;
    this.stagedEndDate = null;
    this.matterSuggestions = [];
    this.matterNotFound = false;
  }

  onConfirmAssignment(): void {
    if (!this.stagedMatter || !this.employeeId || this.isCreatingAssignment)
      return;

    this.isCreatingAssignment = true;
    const matter = this.stagedMatter;
    this.assignmentService
      .createDirectAssignment(
        matter.clientMatterNumber,
        this.employeeId,
        this.stagedStartDate ? this.stagedStartDate.toISOString() : null,
        this.stagedEndDate ? this.stagedEndDate.toISOString() : null,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // No status is sent, so the API creates the row Pending, and the message
          // has to say so rather than read as a finished assignment.
          this.snackbarService.showSuccess(
            `Assigned to ${matter.matterName || 'matter'} as Pending. Mark the assignment Active to confirm it.`,
          );
          this.stagedMatter = null;
          this.stagedStartDate = null;
          this.stagedEndDate = null;
          this.clearAddMatterTypeahead();
          this.isCreatingAssignment = false;
          this.isAddingAssignment = false;
          this.loadPendingAssignments();
        },
        error: (error) => {
          this.snackbarService.showError(
            describeApiError(error, 'Failed to create assignment. Please try again.'),
          );
          this.isCreatingAssignment = false;
        },
      });
  }

  onCancelStagedAssignment(): void {
    this.stagedMatter = null;
    this.stagedStartDate = null;
    this.stagedEndDate = null;
    this.addMatterQuery = '';
  }

  onRemoveAssignment(row: AssignmentRow): void {
    if (!row.assignedResourceId) return;

    const matterLabel =
      row.matterTitle || row.clientMatterNumber || 'this assignment';
    const dialogRef = this.dialog.open<
      ConfirmationDialogComponent,
      ConfirmationDialogData,
      boolean
    >(ConfirmationDialogComponent, {
      width: '420px',
      autoFocus: false,
      data: {
        title: 'Remove Assignment',
        message: `Are you sure you want to remove this resource from ${matterLabel}?`,
        confirmText: 'Remove',
        cancelText: 'Cancel',
        icon: 'warning',
        iconColor: '#f59e0b',
        highlightColor: '#fef3c7',
        confirmColor: 'var(--dark-red-error)',
        cancelVariant: 'destructive',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.assignmentService
        .deleteAssignment(row.assignedResourceId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.snackbarService.showSuccess('Assignment removed.');
            this.loadPendingAssignments();
          },
          error: (error) => {
            this.snackbarService.showError(
              describeApiError(error, 'Failed to remove assignment. Please try again.'),
            );
          },
        });
    });
  }

  /** Called when the assignments card emits a dialog-driven delete (already executed). */
  loadAssignmentsAfterRemove(_row: AssignmentRow): void {
    this.snackbarService.showSuccess('Assignment removed.');
    this.loadPendingAssignments();
  }

  /**
   * Called when the assignments card patches a row in place via the edit dialog.
   * The row needs no reload, but the Assignment Notes card renders the DTOs, so
   * the edited notes and assignment type are carried back onto a new copy of
   * the list for it.
   *
   * A row the list does not hold is one the card created from a synthetic
   * latest-billed row ("Mark Active"): the page has never fetched it, so there
   * is nothing to patch, and the list is refreshed silently to pick it up.
   */
  onAssignmentChanged(row: AssignmentRow): void {
    const updated = applyAssignmentRowEdits(this.pendingAssignments, row);
    if (updated === this.pendingAssignments) {
      this.refreshAssignmentNotesSilently();
      return;
    }
    this.pendingAssignments = updated;
    // A load sent before this edit, silent or full, would land the pre-edit
    // note over it. The server already holds the edit, so a fresh request
    // includes it, and switchMap drops the older one. A full load in flight is
    // re-issued as a full load (see refreshAssignmentNotesSilently).
    if (this.silentRefreshPending || this.isLoadingAssignments) {
      this.refreshAssignmentNotesSilently();
    }
  }

  private performMatterSearch(query: string): void {
    this.matterService
      .searchMatters(query, undefined, undefined, undefined, undefined, 10, 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const assignedMatters = new Set(
            this.assignmentRows
              .map((row) => row.clientMatterNumber)
              .filter((cmn): cmn is string => !!cmn),
          );
          this.matterSuggestions = (response?.matters || []).filter(
            (m) => !assignedMatters.has(m.clientMatterNumber),
          );
          this.matterNotFound = this.matterSuggestions.length === 0;
          this.isSearchingMatters = false;
        },
        error: () => {
          this.matterSuggestions = [];
          this.matterNotFound = true;
          this.isSearchingMatters = false;
        },
      });
  }
}
